import { FileSourcePage, FileSourcePageRange } from '../FileSource';
import { Result } from '../types';

export enum PageRangeErrorCode {
  INVALID_FORMAT = 'INVALID_FORMAT',
  OUT_OF_BOUNDS = 'OUT_OF_BOUNDS',
  DUPLICATE_VALUES = 'DUPLICATE_VALUES',
}

export interface ValidatePageRangeOptions {
  allowMultipleRanges?: boolean;
}

/** Success and error shapes are paired: both `code` and `message` are null, or both are set. */
export type PageRangeValidationResult = { code: null; message: null } | { code: PageRangeErrorCode; message: string };

export class PageRangeParser {
  private totalPages: number;

  constructor(totalPages: number) {
    this.totalPages = totalPages;
  }

  /**
   * Checks if a string contains only allowed characters for page ranges.
   *
   * Allows:
   * - Numbers (0-9)
   * - Commas (,)
   * - Dashes (-)
   * - Spaces
   *
   * Empty strings are considered valid.
   *
   * @param range - The string to check
   * @returns true if the string contains only allowed characters or is empty, false otherwise
   *
   * @example
   * ```typescript
   * const parser = new PageRangeParser(10);
   * parser.allowCharacters("1-5, 8"); // Returns true
   * parser.allowCharacters(""); // Returns true
   * parser.allowCharacters("1-5 abc"); // Returns false (contains letters)
   * parser.allowCharacters("1.5"); // Returns false (contains period)
   * ```
   */
  allowCharacters(range: string): boolean {
    // Only allow numbers, commas and dashes and spaces
    return !range || /^[0-9,\-\s]+$/.test(range);
  }

  /**
   * Validates a page range string and returns validation result.
   *
   * Checks for:
   * - Empty or invalid format (returns INVALID_FORMAT)
   * - Pages out of bounds (must be between 1 and totalPages, returns OUT_OF_BOUNDS)
   * - Duplicate page numbers (returns DUPLICATE_VALUES)
   *
   * The range string can contain:
   * - Single pages: "5"
   * - Page ranges: "1-3"
   * - Multiple comma-separated values: "1-3, 5, 7-9"
   *
   * @param range - The page range string to validate (e.g., "1-5, 8, 11-13")
   * @param options - Validation options
   * @param options.allowMultipleRanges - Whether comma-separated ranges are allowed (default: true)
   * @returns An object with:
   *   - `code`: PageRangeErrorCode if validation failed, null if valid
   *   - `message`: Error message if validation failed, null if valid
   *
   * @example
   * ```typescript
   * const parser = new PageRangeParser(10);
   * parser.validate("1-3, 5"); // Returns { code: null, message: null }
   * parser.validate("15"); // Returns { code: OUT_OF_BOUNDS, message: "Page range must be between 1 and 10" }
   * parser.validate("1, 1"); // Returns { code: DUPLICATE_VALUES, message: "Page range contains duplicate values" }
   * ```
   */
  validate(range: string, { allowMultipleRanges = true }: ValidatePageRangeOptions = {}): PageRangeValidationResult {
    const invalidFormatMessage = allowMultipleRanges
      ? 'Invalid page range, use e.g. 1-5, 8, 11-13'
      : 'Invalid page range, use e.g. 1-5';

    if (!range) {
      return {
        code: PageRangeErrorCode.INVALID_FORMAT,
        message: invalidFormatMessage,
      };
    }

    if (!allowMultipleRanges && range.includes(',')) {
      return {
        code: PageRangeErrorCode.INVALID_FORMAT,
        message: invalidFormatMessage,
      };
    }

    const ranges = range
      .split(',')
      .map((r) => r.trim())
      .filter((r) => r.trim() !== '');

    const seenPages = new Set<number>();

    for (const r of ranges) {
      if (r.includes('-')) {
        const [start, end] = r.split('-').map((num) => Number(num.trim()));

        if (Number.isNaN(start) || Number.isNaN(end) || start > end) {
          return {
            code: PageRangeErrorCode.INVALID_FORMAT,
            message: invalidFormatMessage,
          };
        }

        if (start <= 0 || end > this.totalPages) {
          return {
            code: PageRangeErrorCode.OUT_OF_BOUNDS,
            message: `Page range must be between 1 and ${this.totalPages}`,
          };
        }

        // Check for duplicates within the range
        for (let page = start; page <= end; page++) {
          if (seenPages.has(page)) {
            return {
              code: PageRangeErrorCode.DUPLICATE_VALUES,
              message: 'Page range contains duplicate values',
            };
          }
          seenPages.add(page);
        }

        continue;
      }

      const page = Number(r);

      if (Number.isNaN(page) || page <= 0 || page > this.totalPages) {
        return {
          code: PageRangeErrorCode.OUT_OF_BOUNDS,
          message: `Page range must be between 1 and ${this.totalPages}`,
        };
      }

      // Check for duplicate single page
      if (seenPages.has(page)) {
        return {
          code: PageRangeErrorCode.DUPLICATE_VALUES,
          message: 'Page range contains duplicate values',
        };
      }

      seenPages.add(page);
    }

    return { code: null, message: null };
  }

  /**
   * Parses a page range string and returns an array of 0-based page indices.
   *
   * The range string can contain:
   * - Single pages: "5" -> [4]
   * - Page ranges: "1-3" -> [0, 1, 2]
   * - Multiple comma-separated values: "1-3, 5, 7-9" -> [0, 1, 2, 4, 6, 7, 8]
   *
   * The method validates the input first. If validation fails, returns an error.
   * Duplicate pages are automatically deduplicated, and the result is sorted.
   *
   * @param range - The page range string to parse (e.g., "1-5, 8, 11-13")
   * @returns A Result object:
   *   - Success: `{ data: number[], error: null }` - A sorted array of 0-based page indices
   *   - Error: `{ data: null, error: string }` - An error message if the range string is invalid
   *
   * @example
   * ```typescript
   * const parser = new PageRangeParser(10);
   * parser.toPageIndices("1-3, 5"); // Returns { data: [0, 1, 2, 4], error: null }
   * parser.toPageIndices("1"); // Returns { data: [0], error: null }
   * parser.toPageIndices("invalid"); // Returns { data: null, error: "Page range must be between 1 and 10" }
   * ```
   */
  toPageIndices(range: string): Result<number[]> {
    if (!range.trim()) {
      return { data: [], error: null };
    }

    const validation = this.validate(range);

    if (validation.code) {
      return { data: null, error: validation.message || 'Invalid page range' };
    }

    const ranges = range
      .split(',')
      .map((r) => r.trim())
      .filter((r) => r.trim() !== '');
    const pageIndices = new Set<number>();

    for (const r of ranges) {
      if (r.includes('-')) {
        const [start, end] = r.split('-').map((num) => parseInt(num.trim(), 10));

        if (!isNaN(start) && !isNaN(end) && start > 0 && end <= this.totalPages) {
          for (let i = start; i <= end; i++) {
            pageIndices.add(i - 1); // Convert to 0-based index
          }
        }

        continue;
      }

      const page = parseInt(r, 10);

      if (!isNaN(page) && page > 0 && page <= this.totalPages) {
        pageIndices.add(page - 1); // Convert to 0-based index
      }
    }

    return { data: Array.from(pageIndices).sort((a, b) => a - b), error: null };
  }

  /**
   * Parses a page range string and returns a structured array of pages and page ranges,
   * preserving the comma-separated structure of the input.
   *
   * Unlike `toPageIndices()`, this method preserves the original structure of the input string.
   * Each comma-separated part in the input maps directly to an entry in the returned array:
   * - Single pages: "5" -> [{ pageNumber: 5 }]
   * - Page ranges: "1-3" -> [{ pageRange: [1, 3] }]
   * - Multiple comma-separated values: "1-3, 5, 7-9" -> [{ pageRange: [1, 3] }, { pageNumber: 5 }, { pageRange: [7, 9] }]
   *
   * The method validates the input first. If validation fails, returns an error.
   * Empty strings return null pages with no error.
   *
   * @param range - The page range string to parse (e.g., "1-5, 8, 11-13")
   * @returns A Result object:
   *   - Success: `{ data: (FileSourcePage | FileSourcePageRange)[], error: null }`
   *   - Error: `{ data: null, error: string }`
   *   - Empty string returns success with empty array: `{ data: [], error: null }`
   *
   * @example
   * ```typescript
   * const parser = new PageRangeParser(10);
   * parser.toStructuredPages("1-3, 5"); // Returns { data: [{ pageRange: [1, 3] }, { pageNumber: 5 }], error: null }
   * parser.toStructuredPages("1"); // Returns { data: [{ pageNumber: 1 }], error: null }
   * parser.toStructuredPages(""); // Returns { data: [], error: null }
   * parser.toStructuredPages("invalid"); // Returns { data: null, error: "Invalid page range..." }
   * ```
   */
  toStructuredPages(range: string, shouldValidate = true): Result<(FileSourcePage | FileSourcePageRange)[]> {
    if (!range.trim()) {
      return { data: [], error: null };
    }

    if (shouldValidate) {
      const validation = this.validate(range);

      if (validation.code) {
        return { data: null, error: validation.message || 'Invalid page range' };
      }
    }

    // Parse the range string to preserve comma-separated structure
    // Each comma-separated part maps directly to a pages entry
    const ranges = range.split(',').map((r) => r.trim());
    const pages: (FileSourcePage | FileSourcePageRange)[] = [];

    for (const r of ranges) {
      if (r.includes('-')) {
        // Range: "5-7" -> { pageRange: [5, 7] }
        const [start, end] = r.split('-').map((num) => parseInt(num.trim(), 10));
        if (!isNaN(start) && !isNaN(end) && start > 0 && end <= this.totalPages) {
          pages.push({ pageRange: [start, end] });
        }
      } else {
        // Single page: "3" -> { pageNumber: 3 }
        const page = parseInt(r, 10);
        if (!isNaN(page) && page > 0 && page <= this.totalPages) {
          pages.push({ pageNumber: page });
        }
      }
    }

    return { data: pages, error: null };
  }
}
